import { Action, ActionCreator, createReducer, on, ReducerTypes } from '@ngrx/store';
import { OfferState, PersonState } from './offer.state';
import {
  addFrameworkContract,
  addPerson,
  addTupCustomer,
  calculateOfferSuccess,
  changeContractDuration,
  changeIsExistingCustomer,
  changeOfferAddress,
  changeSaleOfferBegin,
  deselectYouthPackage,
  removeFrameworkContract,
  removeOfferForPerson,
  removePerson,
  resetOfferState,
  selectYouthPackage,
  updateOfferDetails,
  updatePerson,
} from './offer.actions';
import { applyToPersonWithId, removeOfferPricesOnPersons } from './offer.reducer.util';
import {
  mapPricesToProducts,
  mapSupplementaryInsuranceDiscounts,
  supplementaryInsuranceReducers,
} from './supplementary-insurance/reducer';
import { basicInsuranceReducers } from './basic-insurance/reducer';

export const initialOfferState: OfferState = {
  persons: [],
  saleOfferBegin: undefined,
  contractDuration: 'THREE_YEARS',
  totalPrice: 0,
  existingCustomer: false,
  tupCustomer: false,
};

function getPersonIdForNewPerson(persons: PersonState[]): number {
  if (persons.length === 0) {
    return 0;
  }
  return persons[persons.length - 1].personId + 1;
}

export const generalOfferReducer: ReducerTypes<OfferState, ActionCreator[]>[] = [
  on(addPerson, (offerState, payload) => ({
    ...offerState,
    persons: [
      ...offerState.persons,
      {
        personId: getPersonIdForNewPerson(offerState.persons),
        details: payload.details,
        supplementaryInsurances: {},
        totalPrice: 0,
        discounts: {
          basicInsuranceDiscounts: [],
          supplementaryInsuranceDiscounts: {},
        },
      },
    ],
  })),
  on(removePerson, (personState, payload) => {
    const otherPersons = personState.persons.filter((person) => person.personId !== payload.personId);
    return {
      ...personState,
      persons: otherPersons,
      totalPrice: undefined,
    };
  }),
  on(updatePerson, (offerState, payload) => ({
    ...offerState,
    persons: applyToPersonWithId(payload.personId, offerState.persons, (person) => ({
      ...person,
      details: payload.details,
    })),
  })),
  on(removeOfferForPerson, (offerState, payload) => ({
    ...offerState,
    persons: applyToPersonWithId(payload.personId, offerState.persons, (person) => ({
      ...person,
      basicInsurance: undefined,
      supplementaryInsurances: {},
      totalPrice: undefined,
    })),
    totalPrice: undefined,
  })),
  on(calculateOfferSuccess, (offerState, payload) => ({
    ...offerState,
    persons: offerState.persons.map((person, index) => ({
      ...person,
      basicInsurance: person.basicInsurance
        ? {
            ...person.basicInsurance,
            price: payload.persons[index].basicInsurancePrice.price,
          }
        : undefined,
      supplementaryInsurances: mapPricesToProducts(
        person.supplementaryInsurances,
        payload.persons[index].supplementaryInsurancePrices,
      ),
      discounts: {
        basicInsuranceDiscounts: payload.persons[index].basicInsurancePrice.discounts,
        supplementaryInsuranceDiscounts: mapSupplementaryInsuranceDiscounts(
          person.supplementaryInsurances,
          payload.persons[index].supplementaryInsurancePrices,
        ),
      },
      totalPrice: payload.persons[index].totalPrice,
    })),
    totalPrice: payload.totalPrice,
  })),
  on(changeContractDuration, (offerState, payload) => ({
    ...offerState,
    contractDuration: payload.contractDuration,
    persons: removeOfferPricesOnPersons(offerState),
    totalPrice: undefined,
  })),
  on(resetOfferState, () => ({ ...initialOfferState })),
  on(addFrameworkContract, (offerState, payload) => ({
    ...offerState,
    frameworkContract: {
      number: payload.number,
      name: payload.name,
      syriusKey: {
        contractNumber: payload.syriusKey.contractNumber,
        personGroupShortDescription: payload.syriusKey.personGroupShortDescription,
      },
    },
  })),
  on(selectYouthPackage, (offerState, payload) => ({
    ...offerState,
    persons: applyToPersonWithId(payload.personId, offerState.persons, (person) => ({
      ...person,
      youthPackage: payload.level,
    })),
  })),
  on(deselectYouthPackage, (offerState, payload) => ({
    ...offerState,
    persons: applyToPersonWithId(payload.personId, offerState.persons, (person) => ({
      ...person,
      youthPackage: undefined,
    })),
  })),
  on(updatePerson, (offerState, payload) => ({
    ...offerState,
    persons: applyToPersonWithId(payload.personId, offerState.persons, (person) => ({
      ...person,
      details: payload.details,
    })),
  })),

  on(addTupCustomer, (offerState) => ({
    ...offerState,
    tupCustomer: true,
  })),
  on(removeFrameworkContract, (offerState) => ({
    ...offerState,
    frameworkContract: undefined,
  })),
  on(updateOfferDetails, (offerState, payload) => ({
    ...offerState,
    saleOfferBegin: payload.saleOfferBegin,
    address: {
      street: payload.address.street,
      houseNumber: payload.address.houseNumber,
      zipCode: payload.address.zipCode,
      bfsNumber: payload.address.bfsNumber,
      city: payload.address.city,
    },
    existingCustomer: payload.existingCustomer,
  })),
  on(changeIsExistingCustomer, (offerState, payload) => ({
    ...offerState,
    existingCustomer: payload.existingCustomer,
  })),
  on(changeSaleOfferBegin, (offerState, payload) => ({
    ...offerState,
    saleOfferBegin: payload.saleOfferBegin,
  })),
  on(changeOfferAddress, (offerState, payload) => ({
    ...offerState,
    address: payload.address,
    persons: removeOfferPricesOnPersons(offerState),
    totalPrice: undefined,
  })),
];

export const offerReducer = createReducer<OfferState>(
  initialOfferState,
  ...generalOfferReducer,
  ...basicInsuranceReducers,
  ...supplementaryInsuranceReducers,
);

export function reducer(state: OfferState | undefined, action: Action): OfferState {
  return offerReducer(state, action);
}
